import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, RotateCcw, X } from 'lucide-react';
import { useState } from 'react';

import { boatsApi, ownersApi } from '../../lib/api-client';
import {
  DEFAULT_OCCUPANCY_SORT,
  OCCUPANCY_SORTS,
  type OccupancySort,
} from '../../lib/occupancy-sort';
import { queryKeys } from '../../lib/query-keys';

/** Owner-filter value meaning "boats with nobody assigned". Matches the API. */
export const UNASSIGNED_OWNER = 'none';

const SORT_LABELS: Record<OccupancySort, string> = {
  'weeks-desc': 'Most booked weeks',
  'weeks-asc': 'Fewest booked weeks',
  'name-asc': 'Boat name (A–Z)',
};

interface OccupancyFiltersProps {
  orgPid: string;
  /** Selected boat pids; empty means the whole fleet. */
  selectedBoats: string[];
  owner: string;
  sort: OccupancySort;
  onChange: (next: { boats?: string[]; owner?: string; sort?: OccupancySort }) => void;
}

/**
 * Boat, owner and sort controls for the occupancy tab.
 *
 * Boat search narrows the options inside the picker rather than the data itself:
 * the filter is whatever you then select. That keeps the summary cards — which
 * are computed server-side from the filtered fleet — authoritative, and avoids a
 * request per keystroke.
 *
 * The owner list comes from `owners/options`, not the admin owners endpoint: that
 * one returns each owner's magic-link token, which is a bearer credential and has
 * no business reaching a filter dropdown.
 */
export function OccupancyFilters({
  orgPid,
  selectedBoats,
  owner,
  sort,
  onChange,
}: OccupancyFiltersProps) {
  const [open, setOpen] = useState(false);

  const { data: boatsData } = useQuery({
    queryKey: queryKeys.boats.list(orgPid, 'occupancy-filter'),
    queryFn: () => boatsApi.list({ limit: 100 }),
    enabled: !!orgPid,
  });
  const { data: owners } = useQuery({
    queryKey: queryKeys.owners.options(orgPid),
    queryFn: () => ownersApi.options(),
    enabled: !!orgPid,
  });

  const boats = boatsData?.data ?? [];
  const selected = boats.filter((b) => selectedBoats.includes(b.id));
  const isFiltered = selectedBoats.length > 0 || owner !== '';
  const isDefault = !isFiltered && sort === DEFAULT_OCCUPANCY_SORT;

  const toggleBoat = (pid: string) =>
    onChange({
      boats: selectedBoats.includes(pid)
        ? selectedBoats.filter((p) => p !== pid)
        : [...selectedBoats, pid],
    });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-9 w-[220px] justify-between font-normal">
              <span className="truncate">
                {selectedBoats.length === 0
                  ? 'All boats'
                  : `${selectedBoats.length} boat${selectedBoats.length === 1 ? '' : 's'}`}
              </span>
              <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search boats..." />
              <CommandList>
                <CommandEmpty>No boat matches.</CommandEmpty>
                <CommandGroup>
                  {boats.map((boat) => (
                    <CommandItem
                      key={boat.id}
                      value={boat.name}
                      onSelect={() => toggleBoat(boat.id)}
                    >
                      <Check
                        className={`mr-2 size-4 ${
                          selectedBoats.includes(boat.id) ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      <span className="truncate">{boat.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Select
          value={owner === '' ? 'all' : owner}
          onValueChange={(v) => onChange({ owner: v === 'all' ? '' : v })}
        >
          <SelectTrigger className="h-9 w-[200px]">
            <SelectValue placeholder="All owners" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All owners</SelectItem>
            <SelectItem value={UNASSIGNED_OWNER}>No owner assigned</SelectItem>
            {(owners ?? []).map((o) => (
              <SelectItem key={o.pid} value={o.pid}>
                {o.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sort} onValueChange={(v) => onChange({ sort: v as OccupancySort })}>
          <SelectTrigger className="h-9 w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OCCUPANCY_SORTS.map((s) => (
              <SelectItem key={s} value={s}>
                {SORT_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {!isDefault && (
          <Button
            variant="ghost"
            className="h-9 px-2 text-xs"
            onClick={() => onChange({ boats: [], owner: '', sort: DEFAULT_OCCUPANCY_SORT })}
          >
            <RotateCcw className="mr-1 size-3" />
            Reset
          </Button>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((b) => (
            <Badge key={b.id} variant="secondary" className="gap-1 font-normal">
              {b.name}
              <button
                type="button"
                aria-label={`Remove ${b.name}`}
                onClick={() => toggleBoat(b.id)}
                className="hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
